/**
 * The slideshow.
 *
 * Two shows, from one player. "Highlights" picks a watchable set that spans the
 * whole trip; "Everything" plays it all in order. Both run full screen, advance
 * on their own, and are meant to be pointed at a television and left alone.
 *
 * The things that make it feel finished are mostly the things it does *not* do
 * while running: it does not let the screen sleep, it does not stall waiting
 * for the next slide, and it does not show its own controls unless somebody
 * moves the mouse.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "../../components/ui/Button.js";
import { SegmentedControl } from "../../components/ui/SegmentedControl.js";
import { type Candidate, everything, pickHighlights } from "./highlights.js";
import { useSlideshowMedia } from "./useSlideshowMedia.js";
import { openFilePicker } from "../../lib/filePicker.js";
import "./Slideshow.css";

export interface SlideshowItem extends Candidate {
  name: string;
}

export interface SlideshowProps {
  groupId: string;
  items: SlideshowItem[];
  /** Named in the corner while it plays, so a shared screen has context. */
  title?: string;
  onClose: () => void;
}

type Show = "highlights" | "everything";

/** Seconds each slide is held. Six is about right; the rest is taste. */
const SPEEDS = [
  { value: "4", label: "Quick" },
  { value: "6", label: "Steady" },
  { value: "10", label: "Slow" },
];

/** A highlights reel longer than this stops being a highlights reel. */
const HIGHLIGHT_LIMIT = 40;

export function Slideshow({ groupId, items, title, onClose }: SlideshowProps) {
  const [show, setShow] = useState<Show>("highlights");
  const [seconds, setSeconds] = useState("6");
  /*
   * Started and playing are different things.
   *
   * Keying the setup screen on `playing` meant pausing threw you back to the
   * configuration dialog and lost your place — which is not what a pause button
   * has ever meant anywhere. Once a show has started it stays started until it
   * is closed.
   */
  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [index, setIndex] = useState(0);
  const [chromeVisible, setChromeVisible] = useState(true);

  const rootRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const chromeTimer = useRef<number | null>(null);

  const ids = useMemo(
    () =>
      show === "highlights"
        ? pickHighlights(items, HIGHLIGHT_LIMIT)
        : everything(items),
    [items, show],
  );

  const byId = useMemo(
    () => new Map(items.map((item) => [item.id, item])),
    [items],
  );
  const media = useSlideshowMedia(groupId, ids, index);

  const step = useCallback(
    (delta: number) => {
      setIndex((current) => {
        const next = current + delta;
        if (next < 0) return ids.length - 1;
        if (next >= ids.length) return 0;
        return next;
      });
    },
    [ids.length],
  );

  /* ─────────────────────────── Advancing ───────────────────────────────── */

  /*
   * The timer is restarted by the *index* changing, not only by the clock, so
   * skipping forward gives the new slide a full turn rather than the remainder
   * of the last one's.
   */
  useEffect(() => {
    if (!playing || ids.length === 0) return;
    const current = ids[index];

    // A slide that has not arrived does not start its clock. Otherwise a slow
    // connection shows a blank frame for its whole turn and moves on.
    if (current !== undefined && !media.urls.has(current)) return;

    const timer = window.setTimeout(() => step(1), Number(seconds) * 1000);
    return () => window.clearTimeout(timer);
  }, [playing, index, seconds, ids, media.urls, step]);

  /* ─────────────────────────── Full screen ─────────────────────────────── */

  const start = useCallback(() => {
    setStarted(true);
    setPlaying(true);
    // Requested from the click that started the show, which is the only time a
    // browser will grant it.
    void rootRef.current?.requestFullscreen?.().catch(() => {
      // Refused, or unsupported. The show still runs, just in the page.
    });
  }, []);

  useEffect(() => {
    return () => {
      if (document.fullscreenElement)
        void document.exitFullscreen().catch(() => {});
    };
  }, []);

  /*
   * Keep the screen awake.
   *
   * A television or a laptop lock screen arriving four minutes into a show is
   * the single most annoying thing this feature could do, and the wake lock is
   * dropped automatically the moment the tab is hidden, so it cannot be abused.
   */
  useEffect(() => {
    if (!playing) return;
    let sentinel: WakeLockSentinel | null = null;
    let live = true;

    void navigator.wakeLock
      ?.request("screen")
      .then((lock) => {
        if (!live) {
          void lock.release();
          return;
        }
        sentinel = lock;
      })
      .catch(() => {
        // Not supported, or refused because the tab is not visible.
      });

    return () => {
      live = false;
      void sentinel?.release().catch(() => {});
    };
  }, [playing]);

  /* ─────────────────────────── Controls ────────────────────────────────── */

  const revealChrome = useCallback(() => {
    setChromeVisible(true);
    if (chromeTimer.current !== null) window.clearTimeout(chromeTimer.current);
    chromeTimer.current = window.setTimeout(
      () => setChromeVisible(false),
      2500,
    );
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      switch (event.key) {
        case "Escape":
          // Full screen swallows the first Escape itself; this handles the
          // in-page case and the second press.
          if (!document.fullscreenElement) onClose();
          break;
        case " ":
        case "Enter":
          event.preventDefault();
          if (started) setPlaying((value) => !value);
          else start();
          revealChrome();
          break;
        case "ArrowRight":
          event.preventDefault();
          step(1);
          revealChrome();
          break;
        case "ArrowLeft":
          event.preventDefault();
          step(-1);
          revealChrome();
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [started, playing, start, step, onClose, revealChrome]);

  /* ─────────────────────────── Music ───────────────────────────────────── */

  /*
   * A file from this device, played locally. It is never uploaded, never sealed
   * and never leaves the browser — which is the only way to offer music without
   * either hosting a library we have no licence to or storing somebody's
   * record collection in a photo cloud.
   */
  const chooseMusic = useCallback(() => {
    openFilePicker({
      accept: "audio/*",
      onPick: (files: File[]) => {
        const file = files[0];
        if (!file) return;
        audioRef.current?.pause();
        const audio = new Audio(URL.createObjectURL(file));
        audio.loop = true;
        audio.volume = 0.6;
        audioRef.current = audio;
        if (playing) void audio.play().catch(() => {});
      },
    });
  }, [playing]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) void audio.play().catch(() => {});
    else audio.pause();
  }, [playing]);

  useEffect(() => {
    return () => {
      const audio = audioRef.current;
      if (!audio) return;
      audio.pause();
      URL.revokeObjectURL(audio.src);
    };
  }, []);

  /* ─────────────────────────── Interface ───────────────────────────────── */

  const currentId = ids[index];
  const currentUrl = currentId ? media.urls.get(currentId) : undefined;
  const currentItem = currentId ? byId.get(currentId) : undefined;

  if (!started) {
    return createPortal(
      <div
        className="fc-show fc-show--setup"
        role="dialog"
        aria-modal="true"
        aria-label="Slideshow"
      >
        <div className="fc-show__setup" ref={rootRef}>
          <h2>{title ? `Play ${title}` : "Play a slideshow"}</h2>

          <SegmentedControl
            aria-label="Which photos"
            value={show}
            onChange={setShow}
            options={[
              { value: "highlights", label: "Highlights" },
              { value: "everything", label: "Everything" },
            ]}
          />
          <p className="fc-show__hint">
            {show === "highlights"
              ? `The best of each moment, spread across the whole ${title ? "album" : "library"} — ${String(ids.length)} photos.`
              : `Every photo in order — ${String(ids.length)} of them.`}
          </p>

          <SegmentedControl
            aria-label="Speed"
            value={seconds}
            onChange={setSeconds}
            options={SPEEDS}
          />

          <div className="fc-show__setup-actions">
            <Button variant="ghost" onClick={chooseMusic}>
              {audioRef.current ? "Music chosen" : "Add music"}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={ids.length === 0}
              onClick={start}
            >
              Play
            </Button>
          </div>

          <p className="fc-show__hint fc-show__hint--quiet">
            Music plays from this device only — it is never uploaded. To watch
            on a television, cast this browser tab.
          </p>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      className="fc-show"
      ref={rootRef}
      data-chrome={chromeVisible || undefined}
      onPointerMove={revealChrome}
      onClick={revealChrome}
      role="region"
      aria-label="Slideshow"
    >
      <div className="fc-show__stage">
        {ids.map((id, position) => {
          // Only the current slide and its neighbours are rendered; the rest
          // would be thousands of elements holding nothing.
          if (Math.abs(position - index) > 1) return null;
          const url = media.urls.get(id);
          if (!url) return null;
          return (
            <div
              key={id}
              className="fc-show__pane"
              data-current={position === index || undefined}
            >
              {/*
                The same photograph, blown up and blurred, filling whatever the
                frame does not.

                A portrait photograph on a television is two thirds black bars,
                and black bars are the thing that makes a slideshow look like a
                file viewer. Filling them with the picture own colours keeps the
                screen alive and — because it is the same image — never fights
                the photograph for attention.
              */}
              <img
                src={url}
                alt=""
                className="fc-show__wash"
                aria-hidden="true"
              />
              <img
                src={url}
                alt=""
                className="fc-show__slide"
                /*
                 * Six drift variants rather than two directions. Consecutive
                 * slides moving the same way turns the show into a conveyor
                 * belt; the same six in the same order is a pattern you start
                 * to notice. Keying off the position mixes them up enough.
                 */
                data-drift={String(position % 6)}
              />
            </div>
          );
        })}

        {!currentUrl && !media.failed && (
          <p className="fc-show__waiting">Loading…</p>
        )}
        {media.failed && (
          <p className="fc-show__waiting">These photos could not be loaded.</p>
        )}
      </div>

      <div className="fc-show__chrome">
        <div className="fc-show__top">
          <span className="fc-show__title">
            {title ?? currentItem?.name ?? ""}
          </span>
          <span className="fc-show__count fc-tabular">
            {index + 1} / {ids.length}
          </span>
        </div>

        <div className="fc-show__controls">
          <button
            type="button"
            aria-label="Previous photo"
            onClick={() => step(-1)}
          >
            <Glyph path="M15 6l-6 6 6 6" />
          </button>
          <button
            type="button"
            aria-label={playing ? "Pause" : "Play"}
            onClick={() => setPlaying((value) => !value)}
          >
            <Glyph path={playing ? "M9 5v14M15 5v14" : "M8 5l11 7-11 7z"} />
          </button>
          <button type="button" aria-label="Next photo" onClick={() => step(1)}>
            <Glyph path="M9 6l6 6-6 6" />
          </button>
          <button type="button" aria-label="Close slideshow" onClick={onClose}>
            <Glyph path="M6 6l12 12M18 6L6 18" />
          </button>
        </div>

        {/* Restarted by keying on the slide, so it always tracks the real turn. */}
        <div className="fc-show__progress" aria-hidden="true">
          <span
            key={`${String(index)}:${String(currentUrl ?? "")}`}
            style={{ animationDuration: `${seconds}s` }}
            data-running={currentUrl ? "" : undefined}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Glyph({ path }: { path: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d={path} />
    </svg>
  );
}
